import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import DayPicker from 'react-day-picker';
import { fromJS as ImmutableFromJs } from 'immutable';
import Moment from 'moment';
import Configuration from '../../Globals/Scripts/Configuration';
import BindOnce from '../../../Common/Utilities/Decorators/BindOnceDecorator';
import { focusNextElement } from '../../Utilities/FocusUtilities.js';
import AutoScopeStyles from '../../Utilities/Decorators/AutoScopeStylesDecorator';
import SetInitialFocusContext from '../../../Consignments/Consignment/Edit/Contexts/SetInitialFocusContext';
import applyDecoratorsDoNotUseInNewCode from '../../Utilities/Decorators/ApplyDecorators';

class DatePickerView extends React.Component {
    static contextType = SetInitialFocusContext;

    constructor(props) {
        super(props);
        this.state = {
            mouseMode: false,
            internalValue: typeof(props.value) === "string" ? Moment(props.value, Moment.ISO_8601, true).format(Configuration.uiDateFormat) : (props.value ? props.value.get('userInput') : ""),
            style: {visibility: 'hidden', top: '100%'}
        };

        this.input = React.createRef();
        this.container = React.createRef();
        this.daypicker = React.createRef();
    }

    render() {
        return (
            <div className={ this.props.className || 'field' } onBlur={ this.bindOnce(this.onBlur) } style={ {'position': 'relative'} } ref={ this.container }>
                 <input
                    autoFocus = { this.props.autoFocus }
                    ref={ this.input }
                    type='text'
                    autoComplete="off"
                    value={ this.state.internalValue }
                    onFocus={ this.bindOnce(this.onInputFocus) }
                    onClick={ this.bindOnce(this.onInputFocus) }
                    onBlur={ this.bindOnce(this.onInputBlur) }
                    onChange={ this.bindOnce(this.onChange) }
                    disabled={this.props.disabled}
                    tabIndex={this.props.disabled ? -1 : undefined} />
                {
                    this.state.mouseMode ?
                        this.renderDayPicker()
                        : null
                }
            </div>
        );
    }

    renderDayPicker() {
        // add 'selected' css tag to the currently selected day
        const modifiers = {selected: day => this.getCurrentValue().isSame(Moment(day), 'day')};

        return (
            <DayPicker
                ref={ this.daypicker }
                modifiers={ modifiers }
                enableOutsideDays={ true }
                initialMonth={ this.getInitialMonthFromCurrentValue() }
                onDayClick={ this.bindOnce(this.onDayClick) }
                tabIndex={ -1 }
                containerProps={ { style: this.state.style } } />
        )
    }

    getCurrentValue() {
        return Moment(this.state.internalValue, Configuration.acceptableInputDateFormats, true);
    }

    getInitialMonthFromCurrentValue() {
        const currentValue = this.getCurrentValue();
        return currentValue.isValid() ? currentValue.toDate() : this.props.initialMonth || new Date();
    }

    onInputFocus(e) {
        this.setState({ mouseMode: true })
    }

    onInputBlur(e) {
        const inputDate = Moment(this.state.internalValue, Configuration.acceptableInputDateFormats, true);
        if(inputDate.isValid())
            this.setInternalValue(inputDate.format(Configuration.uiDateFormat));
    }

    onChange(e) {
        this.disableMouseMode();
        this.setInternalValue(e.target.value);
    }

    setInternalValue(newInternalValue) {
        this.setState({ internalValue: newInternalValue });
        const inputDate = Moment(newInternalValue, Configuration.acceptableInputDateFormats, true);

        const parsedDate = inputDate.isValid() ? inputDate.format(Configuration.iso8601DateOnlyFormat) : null;
        if(this.props.onChange) {
            this.props.onChange(parsedDate);
        }
        if(this.props.onChange2) {
            this.props.onChange2(ImmutableFromJs({ userInput: newInternalValue, validDate: parsedDate }));
        }
    }

    onBlur(e) {
        if (!this.state.mouseMode){
            return;
        }
        e.persist();
        setTimeout(() => {
            const relatedTarget = e.relatedTarget || document.activeElement;
            const container = this.container.current;
            if (!container) {
                //no such container, must've been unmounted.
                return;
            }
            const clickWasInsideDayPickerOrInput = relatedTarget && relatedTarget === container || container.contains(relatedTarget);

            if (!clickWasInsideDayPickerOrInput) {
                this.disableMouseMode();
            }
        }, 0); //Defer the handling of the event as in firefox the newly focused element is not available when the blur event fires.
    }

    onDayClick(day, e) {
        this.disableMouseMode();
        this.setInternalValue(Moment(day).format(Configuration.uiDateFormat));

        if(this.props.focusOnNextElementAfterSelectingDay) {
            const input = this.input.current;
            setTimeout(focusNextElement.bind(input), 0);
        }
    }

    disableMouseMode() {
        if (this.state.mouseMode) {
            this.setState({mouseMode: false,  style: {visibility: 'hidden', top: '100%'}});
        }
    }

    static getDerivedStateFromProps(props, state)
    {
        const propValue = props.value;
        if(propValue && propValue.get) {
            //assume this is the immutable type as documented against onChange2
            const propUserInput = propValue.get('userInput');
            if(propUserInput === state.internalValue)
                return {};
            return { internalValue: propUserInput };
        }

        if (propValue) {
            const internalMoment = Moment(state.internalValue, Configuration.acceptableInputDateFormats, true);
            const propsMoment = Moment(propValue, Moment.ISO_8601, true);
            if (!internalMoment.isSame(propsMoment)) {
                return {internalValue: propsMoment.format(Configuration.uiDateFormat)};
            }
        }
        return null;
    }

    componentDidUpdate(prevProps, prevState) {
        if(!prevState.mouseMode && this.state.mouseMode && this.state.style.visibility === 'hidden') {
            const daypicker = ReactDOM.findDOMNode(this.daypicker.current); // Must use findDOMNode because daypicker.current is an instance of a mounted component
            const daypickerRect = daypicker.getBoundingClientRect();
            const clientWidth = document.documentElement.clientWidth || window.innerWidth;
            const clientHeight = document.documentElement.clientHeight || window.innerHeight;

            const style = Object.assign({}, this.state.style);
            style.visibility = 'visible';

            if(daypickerRect.bottom > clientHeight) {
                style.top = 'auto';
                style.bottom = '100%';
            }

            if(daypickerRect.right > clientWidth)
                style.marginLeft = `${clientWidth - daypickerRect.right}px`;

            this.setState({style});
        }
    }

    componentDidMount() {
        if(this.context === true) {
            this.input.current.focus();
            this.setState({ mouseMode: false });
        }
    }
}

DatePickerView.propTypes = {
    className: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), //string in the same format as returned by the onChange callback, or object in the same format as returned by the onChange2 callback
    initalMonth: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,     //accepts a string of valid date e.g. "2016-03-01", or null if the current input is invalid
    onChange2: PropTypes.func,    //accepts an object: { userInput: <the current, potentially invalid user input (never null)>, validDate: <the date in YYYY-MM-DD format if valid, otherwise null> }
    focusOnNextElementAfterSelectingDay: PropTypes.bool,
    disabled: PropTypes.bool
};

const withDecorators = applyDecoratorsDoNotUseInNewCode(BindOnce, AutoScopeStyles)

export default withDecorators(DatePickerView);
